import {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useMutation } from '@apollo/client';
import { Map, List } from 'immutable';
import isEmpty from 'lodash/isEmpty';

import { FileChecksum } from '@rails/activestorage/src/file_checksum';

import useBlobUpload from 'helpers/direct_upload/blob_upload';
import useRecognition from 'helpers/recognition';
import useTextExtraction from 'helpers/extract_text';

import { DIRECT_UPLOAD_MUTATION, DIRECT_UPLOAD_RECOGNITION_RESPONSE_MUTATION } from 'queries/prompt/response';
import { DIRECT_UPLOAD_CAMPAIGN_MUTATION, DIRECT_UPLOAD_COMPANY_MUTATION } from 'queries/tenant/direct_upload';
import { DIRECT_UPLOAD_CANDIDATE_RESUME_MUTATION } from 'queries/candidate/direct_upload'

const QUERY_MAP = {
  response: { query: DIRECT_UPLOAD_MUTATION, node: 'directUpload' },
  response_recognize: { query: DIRECT_UPLOAD_RECOGNITION_RESPONSE_MUTATION, node: 'directUpload' },
  campaign: { query: DIRECT_UPLOAD_CAMPAIGN_MUTATION, node: 'directUploadCampaign' },
  company: { query: DIRECT_UPLOAD_COMPANY_MUTATION, node: 'directUploadCompany' },
  candidate_resume: { query: DIRECT_UPLOAD_CANDIDATE_RESUME_MUTATION, node: 'directUploadCandidate' }, // FOR APPLY WITH RESUME
};

const DEFAULT_LEGACY_ACTIVE_STORAGE = ['response', 'response_recognize', 'candidate_resume']; // Means the it will be saved in the Documennt Bucket
const OBJECT_PLACEHOLDER = new Map({
  filename: '',
  contentType: '',
  byteSize: '',
  checksum: '',
});

function DirectUpload(blob, key, documentName, country) {
  const [state, setState] = useState(new Map());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recognitionProgress, setRecognitionProgress] = useState(0);
  const [textExtractionProgress, setTextExtractionProgress] = useState(0);

  const variable = state.get('variable', null);
  const metadata = state.get('metadata', null);

  const result = state.get('result', null);
  const processError = state.get('processError', null);
  const finished = state.get('result', false);

  const [setHash] = useBlobUpload();
  const [recognize] = useRecognition();
  const [extractText] = useTextExtraction();

  const [directUpload, { data, error }] = useMutation(QUERY_MAP[key].query, {
    onError: (err) => {
      setState(state.set('result', { processError: { upload: true } }).set('finished', true));
      console.error('mutation-on-error', err);
    },
  });

  useEffect(() => {
    if (isEmpty(blob)) return;

    setState(new Map());
    setUploadProgress(0);
    setRecognitionProgress(0);
    setTextExtractionProgress(0);

    FileChecksum.create(blob, (err, checksum) => {
      const extension = blob.type.split('/').pop();
      const filename = blob.name ? blob.name : `file-${Date.now()}.${extension}`;

      if (err || checksum === '') {
        console.error('file-checksum-blob-record-error', err);
        Sentry.captureException(err, { tags: { filename, size: blob.size, type: blob.type } });
        return;
      }

      let newvar = OBJECT_PLACEHOLDER.set('checksum', checksum)
        .set('filename', filename)
        .set('byteSize', blob.size)
        .set('contentType', blob.type);

      if (!DEFAULT_LEGACY_ACTIVE_STORAGE.includes(key)) newvar = newvar.set('serviceName', 'awscdn');

      setState(state => state.set('variable', newvar));
    });
  }, [blob]);

  useEffect(() => {
    if (isEmpty(variable)) return;

    const vars = {
      variables: {
        file: [variable.toJS()],
      }
    };

    directUpload(vars);
  }, [variable]);

  useEffect(() => {
    if (!data) return;

    const { metadata } = data.tenant[QUERY_MAP[key].node];
    setState(state.set('metadata', metadata[0]));
  }, [data]);

  useEffect(() => {
    if (!error) return;

    setState(state.set('processError', { upload: true }).set('finished', true));
  }, [data]);

  useEffect(() => {
    if (!metadata) return;

    const promises = [setHash(metadata, blob, setUploadProgress)];

    // TODO: Combine / Optimize
    if (metadata.recognition && metadata.recognition.url) {
      promises.push(recognize(metadata.recognition.url, JSON.parse(metadata.recognition.headers), JSON.parse(metadata.recognition.body), blob, documentName, country, setRecognitionProgress));
    } else {
      promises.push(new Promise((resolve) => resolve({})));
    }

    if (metadata.extractText && metadata.extractText.url) {
      promises.push(extractText(metadata.extractText.url, JSON.parse(metadata.extractText.headers), JSON.parse(metadata.extractText.body), blob, setTextExtractionProgress));
    } else {
      promises.push(new Promise((resolve) => resolve({})));
    }

    Promise.all(promises).then((values) => {
      const error = {
        upload: values[0].error,
        recognition: values[1].error,
        textExtraction: values[2].error,
      }

      const result = {
        ...metadata,
        recognition: values[1],
        textExtraction: values[2],
      }

      setState(state.set('result', result).set('processError', error).set('finished', true));
    });
  }, [metadata]);

  const progress = useMemo(() => {
    if (!metadata) return 0;

    const actions = [uploadProgress];
    if (metadata.recognition) actions.push(recognitionProgress);
    if (metadata.extractText) actions.push(textExtractionProgress);

    return Math.round(actions.reduce((sum, progress) => sum + progress / actions.length, 0));
  }, [uploadProgress, recognitionProgress, textExtractionProgress, metadata]);

  return [result, processError, finished, progress];
}

export default DirectUpload;
